/**
 * Scenario Switcher Controller (EPIC-026.6)
 *
 * Wires the page-head `<select>` (load saved scenarios), the "Sauvegarder"
 * button (open the save dialog → POST /simulator/scenarios), the 409
 * `scenario_limit_reached` dialog, and the manage dialog (rename/archive
 * inline). Consumes `GET|POST|PATCH|DELETE /simulator/scenarios` from
 * EPIC-026.4.
 *
 * Targets are all under the same wrapper as `simulator-form`, which lets us
 * read/write the form state through `serializeSimulatorForm` /
 * `hydrateSimulatorForm` from `utils/simulator_serializer.js`. The form's own
 * input/change listener picks up the hydrated values and re-runs the
 * debounced /calculate request — we just dispatch a synthetic `change` event
 * once hydration is done.
 *
 * No HTML built in JS — every dynamic node is cloned from a `<template>`.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";
import {
  serializeSimulatorForm,
  hydrateSimulatorForm,
} from "../utils/simulator_serializer.js";

export default class ScenarioSwitcherController extends BaseController {
  static targets = [
    "select",
    "saveDialog",
    "saveTitleInput",
    "saveError",
    "saveSubmit",
    "limitDialog",
    "manageDialog",
    "manageList",
    "manageEmpty",
  ];

  connect() {
    this.scenarios = [];
    this.refreshScenarios();
  }

  // ---------------------------------------------------------------------------
  // Listing
  // ---------------------------------------------------------------------------

  async refreshScenarios() {
    try {
      const res = await api.get("/simulator/scenarios", { perPage: 100 });
      const items = Array.isArray(res?.data) ? res.data : [];
      this.scenarios = items.filter((s) => s.status === "active");
      this.renderSelect();
      this.renderManageList();
    } catch (_e) {
      // List failure is silent — switcher just stays empty.
    }
  }

  renderSelect() {
    if (!this.hasSelectTarget) return;
    const select = this.selectTarget;
    const previousValue = select.value;

    while (select.firstChild) select.removeChild(select.firstChild);
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "Simulation courante";
    select.appendChild(placeholder);

    this.scenarios.forEach((s) => {
      const fragment = this.cloneTemplate("tpl-scenario-select-option");
      const opt = fragment.querySelector("option");
      if (opt) {
        opt.value = s.id;
        opt.textContent = s.title;
      }
      select.appendChild(fragment);
    });

    if (this.scenarios.length > 0) {
      const manage = document.createElement("option");
      manage.value = "__manage__";
      manage.textContent = "Gérer mes scénarios…";
      select.appendChild(manage);
    }

    if (previousValue && this.scenarios.some((s) => s.id === previousValue)) {
      select.value = previousValue;
    } else {
      select.value = "";
    }
  }

  // ---------------------------------------------------------------------------
  // Load (switcher)
  // ---------------------------------------------------------------------------

  async onSelectChange(event) {
    const value = event.currentTarget.value;
    if (value === "") {
      return;
    }
    if (value === "__manage__") {
      event.currentTarget.value = this._currentSelectionId() ?? "";
      this.openManageDialog();
      return;
    }
    await this.loadScenario(value);
  }

  async loadScenario(scenarioId) {
    try {
      const scenario = await api.get(`/simulator/scenarios/${encodeURIComponent(scenarioId)}`);
      if (!scenario || !scenario.inputs) {
        this.showError("Scénario introuvable ou corrompu.");
        return;
      }
      this._applyInputsToForm(scenario.inputs);
      this._currentScenarioId = scenario.id;
      this.showSuccess(`Scénario « ${scenario.title} » chargé.`);
    } catch (error) {
      this.showError(this._messageForError(error, "Impossible de charger le scénario."));
    }
  }

  _applyInputsToForm(inputs) {
    const formRoot = document.querySelector('[data-controller~="simulator-form"]');
    if (!formRoot) return;
    hydrateSimulatorForm(formRoot, inputs);
    formRoot.dispatchEvent(new Event("change", { bubbles: true }));
  }

  _currentSelectionId() {
    return this._currentScenarioId ?? null;
  }

  // ---------------------------------------------------------------------------
  // Save dialog
  // ---------------------------------------------------------------------------

  openSaveDialog() {
    if (!this.hasSaveDialogTarget) return;
    if (this.hasSaveErrorTarget) {
      this.saveErrorTarget.textContent = "";
      this.saveErrorTarget.hidden = true;
    }
    if (this.hasSaveTitleInputTarget) {
      this.saveTitleInputTarget.value = this._suggestTitle();
    }
    if (this.hasSaveSubmitTarget) this.saveSubmitTarget.disabled = false;
    this.saveDialogTarget.showModal();
    if (this.hasSaveTitleInputTarget) {
      this.saveTitleInputTarget.focus();
      this.saveTitleInputTarget.select();
    }
  }

  closeSaveDialog() {
    if (this.hasSaveDialogTarget && this.saveDialogTarget.open) {
      this.saveDialogTarget.close();
    }
  }

  async submitSave(event) {
    event.preventDefault();
    if (!this.hasSaveTitleInputTarget) return;
    const title = this.saveTitleInputTarget.value.trim();
    if (title === "") {
      this._showSaveError("Donnez un nom au scénario.");
      return;
    }
    if (title.length > 120) {
      this._showSaveError("120 caractères maximum.");
      return;
    }

    if (this.hasSaveSubmitTarget) this.saveSubmitTarget.disabled = true;

    const formRoot = document.querySelector('[data-controller~="simulator-form"]');
    if (!formRoot) {
      this._showSaveError("Formulaire introuvable.");
      if (this.hasSaveSubmitTarget) this.saveSubmitTarget.disabled = false;
      return;
    }
    const inputs = serializeSimulatorForm(formRoot);

    let outputs;
    try {
      const res = await api.post("/simulator/calculate", inputs);
      outputs = (res && res.data) || res;
    } catch (error) {
      this._showSaveError(this._messageForError(error, "Calcul impossible — vérifiez les valeurs saisies."));
      if (this.hasSaveSubmitTarget) this.saveSubmitTarget.disabled = false;
      return;
    }

    try {
      const created = await api.post("/simulator/scenarios", { title, inputs, outputs });
      this.closeSaveDialog();
      this.showSuccess(`Scénario « ${created.title} » sauvegardé.`);
      this._currentScenarioId = created.id;
      await this.refreshScenarios();
      if (this.hasSelectTarget) this.selectTarget.value = created.id;
    } catch (error) {
      if (error instanceof APIError && error.status === 409) {
        this.closeSaveDialog();
        this.openLimitDialog();
        return;
      }
      this._showSaveError(this._messageForError(error, "Échec de la sauvegarde."));
      if (this.hasSaveSubmitTarget) this.saveSubmitTarget.disabled = false;
    }
  }

  _showSaveError(message) {
    if (!this.hasSaveErrorTarget) return;
    this.saveErrorTarget.textContent = message;
    this.saveErrorTarget.hidden = false;
  }

  _suggestTitle() {
    const now = new Date();
    const month = now.toLocaleDateString("fr-FR", { month: "long" });
    return `Scénario du ${now.getDate()} ${month}`;
  }

  // ---------------------------------------------------------------------------
  // Limit dialog (409)
  // ---------------------------------------------------------------------------

  openLimitDialog() {
    if (this.hasLimitDialogTarget) this.limitDialogTarget.showModal();
  }

  closeLimitDialog() {
    if (this.hasLimitDialogTarget && this.limitDialogTarget.open) {
      this.limitDialogTarget.close();
    }
  }

  openManageDialogFromLimit() {
    this.closeLimitDialog();
    this.openManageDialog();
  }

  // ---------------------------------------------------------------------------
  // Manage dialog (list with rename + archive)
  // ---------------------------------------------------------------------------

  openManageDialog() {
    if (!this.hasManageDialogTarget) return;
    this.renderManageList();
    this.manageDialogTarget.showModal();
  }

  closeManageDialog() {
    if (this.hasManageDialogTarget && this.manageDialogTarget.open) {
      this.manageDialogTarget.close();
    }
  }

  renderManageList() {
    if (!this.hasManageListTarget) return;
    const list = this.manageListTarget;
    while (list.firstChild) list.removeChild(list.firstChild);

    if (this.scenarios.length === 0) {
      if (this.hasManageEmptyTarget) this.manageEmptyTarget.hidden = false;
      return;
    }
    if (this.hasManageEmptyTarget) this.manageEmptyTarget.hidden = true;

    this.scenarios.forEach((s) => {
      const fragment = this.cloneTemplate("tpl-scenario-row");
      const article = fragment.querySelector("article");
      if (article) article.dataset.scenarioId = s.id;
      this.fillSlots(fragment, {
        title: s.title,
        createdAt: this._formatCreatedAt(s.createdAt),
        summary: this._summarizeScenario(s),
      });
      list.appendChild(fragment);
    });
  }

  _formatCreatedAt(iso) {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    } catch (_e) {
      return "";
    }
  }

  _summarizeScenario(s) {
    const inputs = s.inputs || {};
    const horizon = inputs.simulationMonths ? `${inputs.simulationMonths} mois` : null;
    const activities = Array.isArray(inputs.activities) ? inputs.activities.length : 0;
    const acre = inputs.acreEligible ? "ACRE" : null;
    const vl = inputs.vlOption ? "VL" : null;
    const parts = [
      activities > 0 ? `${activities} activité${activities > 1 ? "s" : ""}` : null,
      horizon,
      acre,
      vl,
    ].filter(Boolean);
    return parts.join(" · ");
  }

  // ---------------------------------------------------------------------------
  // Row actions
  // ---------------------------------------------------------------------------

  async loadFromList(event) {
    const id = this._scenarioIdFromEvent(event);
    if (!id) return;
    this.closeManageDialog();
    if (this.hasSelectTarget) this.selectTarget.value = id;
    await this.loadScenario(id);
  }

  startRename(event) {
    const article = event.currentTarget.closest("[data-scenario-id]");
    if (!article) return;
    const titleEl = article.querySelector('[data-scenario-switcher-target="rowTitle"]');
    const input = article.querySelector('[data-scenario-switcher-target="rowRenameInput"]');
    if (!titleEl || !input) return;
    input.value = titleEl.textContent || "";
    input.hidden = false;
    titleEl.hidden = true;
    input.focus();
    input.select();
    this._toggleRowButtons(article, true);
  }

  cancelRename(event) {
    const article = event.currentTarget.closest("[data-scenario-id]");
    if (!article) return;
    const titleEl = article.querySelector('[data-scenario-switcher-target="rowTitle"]');
    const input = article.querySelector('[data-scenario-switcher-target="rowRenameInput"]');
    if (!titleEl || !input) return;
    input.hidden = true;
    titleEl.hidden = false;
    this._toggleRowButtons(article, false);
  }

  async confirmRename(event) {
    const article = event.currentTarget.closest("[data-scenario-id]");
    if (!article) return;
    const id = article.dataset.scenarioId;
    const input = article.querySelector('[data-scenario-switcher-target="rowRenameInput"]');
    if (!id || !input) return;
    const title = input.value.trim();
    if (title === "" || title.length > 120) {
      this.showError("Titre invalide (1 à 120 caractères).");
      return;
    }
    try {
      const updated = await api.patch(`/simulator/scenarios/${encodeURIComponent(id)}`, { title });
      const scenario = this.scenarios.find((s) => s.id === id);
      if (scenario) scenario.title = updated.title;
      this.renderSelect();
      this.renderManageList();
      this.showSuccess("Scénario renommé.");
    } catch (error) {
      this.showError(this._messageForError(error, "Renommage impossible."));
    }
  }

  async archiveScenario(event) {
    const article = event.currentTarget.closest("[data-scenario-id]");
    if (!article) return;
    const id = article.dataset.scenarioId;
    if (!id) return;

    const ok = await this.confirm("Archiver ce scénario ? Il sera retiré de la liste.", {
      title: "Archiver le scénario",
      confirmLabel: "Archiver",
      cancelLabel: "Annuler",
    });
    if (!ok) return;

    try {
      await api.delete(`/simulator/scenarios/${encodeURIComponent(id)}`);
      this.scenarios = this.scenarios.filter((s) => s.id !== id);
      if (this._currentScenarioId === id) this._currentScenarioId = null;
      this.renderSelect();
      this.renderManageList();
      this.showSuccess("Scénario archivé.");
    } catch (error) {
      this.showError(this._messageForError(error, "Archivage impossible."));
    }
  }

  _toggleRowButtons(article, renaming) {
    const buttons = article.querySelectorAll("[data-action]");
    buttons.forEach((btn) => {
      const action = btn.dataset.action || "";
      const isStart = action.includes("#startRename");
      const isConfirm = action.includes("#confirmRename");
      const isCancel = action.includes("#cancelRename");
      const isLoad = action.includes("#loadFromList");
      const isArchive = action.includes("#archiveScenario");
      if (isStart || isLoad || isArchive) btn.hidden = renaming;
      if (isConfirm || isCancel) btn.hidden = !renaming;
    });
  }

  _scenarioIdFromEvent(event) {
    const article = event.currentTarget.closest("[data-scenario-id]");
    return article?.dataset?.scenarioId ?? null;
  }

  _messageForError(error, fallback) {
    if (error instanceof APIError) {
      return error.message || fallback;
    }
    return fallback;
  }
}
